import Gl from '../../../Gl'

import { RenderTarget, Camera, Transform, Orbit, Vec3, Box, Mesh, Sphere, Plane, Vec2, Program, Color } from 'ogl'
import cloneGLTF from '../../../Utils/CloneGLTF'
import { gaussianBlur, readDepth, blendOverlay, rotateUV, add } from '../../../Utils/ShaderChunks'

export default class Background {
  constructor(_params) {
    this.gl = new Gl()

    /* 
      Flags
    */
    this.isRendering = true

    /* 
      Scene
    */
    this.scene = new Transform()

    /* 
      Params
    */
    this.params = _params

    /* 
      Render Target
    */
    this.renderTarget = new RenderTarget(this.gl.renderer.instance.gl, {
      width: this.gl.sizes.width * this.gl.sizes.pixelRatio,
      height: this.gl.sizes.height * this.gl.sizes.pixelRatio,
    })

    /* 
      Instance 0
    */
    this.instance0 = {
      mesh: new Mesh(this.gl.renderer.instance.gl, {
        geometry: new Plane(this.gl.renderer.instance.gl),
        program: new Program(this.gl.renderer.instance.gl, {
          uniforms: {
            uResolution: { value: new Vec2(this.gl.sizes.width, this.gl.sizes.height) },
            uAspect: { value: this.gl.sizes.aspect },
            uTime: { value: 0 },

            tBlurryObject: { value: this.gl.assets.textures.blur },
            tBlurryObjectGradient: { value: this.gl.assets.textures.blurGradient },

            uBlurryObjectsOffset0: { value: new Vec2(0, 0) },
            uBlurryObjectsOffset1: { value: new Vec2(0, 0) },
            uBlurryObjectScale0: { value: 0.33 },
            uBlurryObjectScale1: { value: 0.6 },
            uBlurryObjectsPosition0: { value: new Vec2(0.0, 0.0) },
            uBlurryObjectsPosition1: { value: new Vec2(0.0, 0.0) },
            uBlurryObjectPositionAnimation0: { value: new Vec2(0, 0) },
            uBlurryObjectPositionAnimation1: { value: new Vec2(0, 0) },
            uBlurryObjectScaleAnimation0: { value: 0 },
            uBlurryObjectScaleAnimation1: { value: 0 },

            uBlurryObjectsOffsetFromSide: { value: 0 },

            COLOR_OBJECT_0_A: { value: new Color(0xffffff) },
            COLOR_OBJECT_0_B: { value: new Color(0xffffff) },
            COLOR_OBJECT_1: { value: new Color(0xffffff) },
          },
          vertex: /* glsl */ `
            attribute vec2 uv;
            attribute vec3 position;

            varying vec2 vUv;

            void main() {
              vUv = uv;

              gl_Position = vec4(position.xy * 2.0, 0.0, 1.0);
            }
          `,
          fragment: /* glsl */ `
            precision highp float;

            varying vec2 vUv;
            uniform vec2 uResolution;
            uniform float uAspect;

            uniform sampler2D tBlurryObject;
            uniform sampler2D tBlurryObjectGradient;

            uniform vec2 uBlurryObjectsOffset0;
            uniform vec2 uBlurryObjectsOffset1;

            uniform vec2 uBlurryObjectsPosition0;
            uniform vec2 uBlurryObjectsPosition1;

            uniform float uBlurryObjectScale0;
            uniform float uBlurryObjectScale1;

            uniform vec2 uBlurryObjectPositionAnimation0;
            uniform vec2 uBlurryObjectPositionAnimation1;

            uniform float uBlurryObjectScaleAnimation0;
            uniform float uBlurryObjectScaleAnimation1;

            uniform float uBlurryObjectsOffsetFromSide;

            uniform vec3 COLOR_OBJECT_0_A;
            uniform vec3 COLOR_OBJECT_0_B;
            uniform vec3 COLOR_OBJECT_1;

            ${rotateUV}
            ${add}
            ${readDepth}
            ${gaussianBlur}

            void main() {
              vec2 uvN = vec2(vUv.x * uAspect, vUv.y);

              vec2 uvBlurryObject0 = rotateUV((vec2(uvN.x - uBlurryObjectsOffsetFromSide, uvN.y) + uBlurryObjectsPosition0) * (uBlurryObjectScale0 + uBlurryObjectScaleAnimation0) + uBlurryObjectsOffset0 + uBlurryObjectPositionAnimation0, radians(-45.0), vec2(0.5, 0.5));
              // uvBlurryObject0 = uvN;
              vec2 uvBlurryObject1 = rotateUV((vec2(uvN.x - uBlurryObjectsOffsetFromSide, uvN.y) + uBlurryObjectsPosition1) * (uBlurryObjectScale1 + uBlurryObjectScaleAnimation1) + uBlurryObjectsOffset1 + uBlurryObjectPositionAnimation1, radians(-45.0), vec2(0.5, 0.5));

              float blurryObjectTexture0 = texture2D(tBlurryObject, uvBlurryObject0).a;
              float blurryObjectTexture1 = texture2D(tBlurryObject, uvBlurryObject1).a;
              float blurryObjectGradient = texture2D(tBlurryObjectGradient, uvBlurryObject1).r;

              vec4 blurryObject0 = vec4(mix(COLOR_OBJECT_0_B, COLOR_OBJECT_0_A, blurryObjectGradient), blurryObjectTexture1);
              vec4 blurryObject1 = vec4(COLOR_OBJECT_1, blurryObjectTexture0);

              vec4 blurryObjects = add(blurryObject0, blurryObject1);
            
              gl_FragColor = blurryObjects;
            }
          `,
        }),
      }),
    }

    /* 
      Instance 1
    */
    this.instance1 = {
      mesh: new Mesh(this.gl.renderer.instance.gl, {
        geometry: new Plane(this.gl.renderer.instance.gl),
        program: new Program(this.gl.renderer.instance.gl, {
          uniforms: {
            uResolution: { value: new Vec2(this.gl.sizes.width, this.gl.sizes.height) },
            uAspect: { value: this.gl.sizes.aspect },
            uTime: { value: 0 },

            uMousePosition: { value: new Vec2(0, 0) },

            uIsFull: { value: 0 },

            tNoise: { value: this.gl.assets.textures.noise },

            COLOR_BACKROUND_GRADIENT_0: { value: new Color(0xffffff) },
            COLOR_BACKROUND_GRADIENT_1: { value: new Color(0xffffff) },
            COLOR_BACKROUND_GRADIENT_2: { value: new Color(0xffffff) },
          },
          vertex: /* glsl */ `
            attribute vec2 uv;
            attribute vec3 position;

            varying vec2 vUv;

            void main() {
              vUv = uv;

              gl_Position = vec4(position.xy * 2.0, 0.0, 1.0);
            }
          `,
          fragment: /* glsl */ `
            precision highp float;

            varying vec2 vUv;
            uniform vec2 uResolution;
            uniform float uAspect;
            uniform float uTime;

            uniform float uIsFull;

            uniform vec2 uMousePosition;

            uniform sampler2D tNoise;

            uniform vec3 COLOR_BACKROUND_GRADIENT_0;
            uniform vec3 COLOR_BACKROUND_GRADIENT_1;
            uniform vec3 COLOR_BACKROUND_GRADIENT_2;

            void main() {
              vec2 uvN = vec2(vUv.x * uAspect, vUv.y);
              
              vec3 textureNoise = texture2D(tNoise, vec2(uvN.x * 0.25 + uTime, 0.0)).rgb;

              vec2 backgroundUv = uvN;
              backgroundUv.y -= (textureNoise.b * 2.0 - 1.0) * 0.5;

              float mask = smoothstep(0.8 - textureNoise.r * 0.25, 0.25 + textureNoise.g * 0.25, backgroundUv.y);
              mask *= 1.0 - pow(length(vUv.x - 0.5 - uMousePosition.x * 0.1) * 2.0, 2.0);
              // mask += smoothstep(0., 0.0, vUv.y);

              vec3 colorGradientA = mix(COLOR_BACKROUND_GRADIENT_0, COLOR_BACKROUND_GRADIENT_1, smoothstep(0.33, 0.5, vUv.x));
              vec3 colorGradientB = mix(COLOR_BACKROUND_GRADIENT_1, COLOR_BACKROUND_GRADIENT_2, smoothstep(0.5, 0.75, vUv.x));
              vec3 colorGradient = mix(colorGradientA, colorGradientB, step(0.5, vUv.x));

              vec3 colorSoft = mix(vec3(1.0), mix(COLOR_BACKROUND_GRADIENT_1, COLOR_BACKROUND_GRADIENT_2, 0.5), mask);
              vec3 colorFull = mix(vec3(1.0), colorGradient, mask);
            
              gl_FragColor = vec4(mix(colorSoft, colorFull, uIsFull), mask);
            }
          `,
        }),
      }),
    }

    /* 
      Instances
    */
    this.instances = [this.instance0, this.instance1]

    /* 
      Shift without objects
    */
    this.shiftWithoutObjects = {
      desktop: {
        a: new Vec2(0, 0),
        b: new Vec2(0, 0),
      },
      mobile: {
        a: new Vec2(0, 0),
        b: new Vec2(0, 0),
      },
    }

    /* 
      Colors
    */
    this.colors = [
      //
      [new Color(0x01b7fd), new Color(0x004cf9), new Color(0xffe5ff)],
      [new Color(0x7024ff), new Color(0x8f44ff), new Color(0xf15eda)],
      [new Color(0xea0021), new Color(0xea0021), new Color(0xfca149)],
      [new Color(0x00e400), new Color(0x00e400), new Color(0xfb7ae1)],
      [new Color(0x01b7fd), new Color(0x004cf9), new Color(0xf6a04f)],
      [new Color(0x01b7fd), new Color(0x004cf9), new Color(0xffa1f8)],
    ]

    this.backgroundColors = [
      [new Color(0x9a56ff), new Color(0x5e81fe), new Color(0xaddeff)],
      [new Color(0xa358ff), new Color(0xd974ff), new Color(0xfbe2ff)],
      [new Color(0xf1314b), new Color(0xffaaaa), new Color(0xffecb1)],
      [new Color(0xf47adc), new Color(0x6cff8a), new Color(0xf47adc)],
      [new Color(0xffbd84), new Color(0x4e98ff), new Color(0xe2e0ef)],
      [new Color(0xedc9ff), new Color(0x2d88ff), new Color(0xedc9ff)],
    ]

    /* 
      Set default
    */
    this.setDefaults()
    this.setVariant('full-with-model')
    this.setColor(0)
  }

  setDefaults() {
    this.setObjectPositions()

    // const rootStyles = getComputedStyle(document.documentElement)
    // console.log(rootStyles.getPropertyValue('--_layout---container--max-width').trim())

    const selector = document.querySelector('.cc-nav')
    const maxWidth = selector ? selector.clientWidth : this.gl.sizes.width * 0.917

    this.instance0.mesh.program.uniforms.uBlurryObjectsOffsetFromSide.value = maxWidth ? ((window.innerWidth - maxWidth) / window.innerWidth / 2) * this.gl.sizes.aspect : 1286
    // console.log(document.querySelector('.container').clientWidth)
  }

  setObjectPositions() {
    if (window.innerWidth > 991) {
      this.instance0.mesh.program.uniforms.uBlurryObjectsPosition0.value.set(-0.2 - this.shiftWithoutObjects.desktop.a.x, 1.0 + this.shiftWithoutObjects.desktop.a.y)
      this.instance0.mesh.program.uniforms.uBlurryObjectsPosition1.value.set(-0.4 + this.shiftWithoutObjects.desktop.b.x, 0.75 + this.shiftWithoutObjects.desktop.b.y)

      this.instance0.mesh.program.uniforms.uBlurryObjectScale0.value = 0.33
      this.instance0.mesh.program.uniforms.uBlurryObjectScale1.value = 0.6
    } else {
      this.instance0.mesh.program.uniforms.uBlurryObjectsPosition0.value.set(0.05 - this.shiftWithoutObjects.mobile.a.x, 0.7 + this.shiftWithoutObjects.mobile.a.y)
      this.instance0.mesh.program.uniforms.uBlurryObjectsPosition1.value.set(0.1 + this.shiftWithoutObjects.mobile.b.x, 0.5 + this.shiftWithoutObjects.mobile.b.y)

      this.instance0.mesh.program.uniforms.uBlurryObjectScale0.value = 0.33 * 1.75
      this.instance0.mesh.program.uniforms.uBlurryObjectScale1.value = 0.6 * 1.5
    }
  }

  setColor(_index) {
    this.instance0.mesh.program.uniforms.COLOR_OBJECT_0_A.value = this.colors[_index][0]
    this.instance0.mesh.program.uniforms.COLOR_OBJECT_0_B.value = this.colors[_index][1]
    this.instance0.mesh.program.uniforms.COLOR_OBJECT_1.value = this.colors[_index][2]

    this.instance1.mesh.program.uniforms.COLOR_BACKROUND_GRADIENT_0.value = this.backgroundColors[_index][0]
    this.instance1.mesh.program.uniforms.COLOR_BACKROUND_GRADIENT_1.value = this.backgroundColors[_index][1]
    this.instance1.mesh.program.uniforms.COLOR_BACKROUND_GRADIENT_2.value = this.backgroundColors[_index][2]
  }

  setVariant(_variant) {
    if (_variant === 'full-with-model') {
      this.active = this.instance0

      this.shiftWithoutObjects.desktop.a.set(0, 0)
      this.shiftWithoutObjects.desktop.b.set(0, 0)
      this.shiftWithoutObjects.mobile.a.set(0, 0)
      this.shiftWithoutObjects.mobile.b.set(0, 0)

      this.setObjectPositions()
      // } else if (_variant === 'full-without-model') {
    } else if (_variant === 'bottom_full') {
      this.active = this.instance0

      this.shiftWithoutObjects.desktop.a.set(0.3, 0)
      this.shiftWithoutObjects.desktop.b.set(1.2, 0)
      this.shiftWithoutObjects.mobile.a.set(0.2, -0.5)
      this.shiftWithoutObjects.mobile.b.set(0.6, 0)

      this.setObjectPositions()
    } else if (_variant === 'bottom_soft') {
      this.active = this.instance1

      this.active.mesh.program.uniforms.uIsFull.value = 0
    }
    // else if (_variant === 'bottom_full') {
    //   this.active = this.instance1

    //   this.active.mesh.program.uniforms.uIsFull.value = 1
    // }
  }

  resize() {
    this.renderTarget.setSize(this.gl.sizes.width * this.gl.sizes.pixelRatio, this.gl.sizes.height * this.gl.sizes.pixelRatio)

    this.active.mesh.program.uniforms.uResolution.value.set(this.gl.sizes.width, this.gl.sizes.height)
    this.active.mesh.program.uniforms.uAspect.value = this.gl.sizes.aspect

    this.setDefaults()
  }

  renderPipeline() {
    if (!this.isRendering) return

    this.gl.renderer.instance.render({
      scene: this.active.mesh,
      target: this.renderTarget,
    })
  }

  update() {
    if (this.active.mesh.program.uniforms.uBlurryObjectPositionAnimation0) {
      this.active.mesh.program.uniforms.uBlurryObjectPositionAnimation0.value.set(Math.sin(-this.gl.time.elapsed * 0.25) * 0.05, Math.cos(-this.gl.time.elapsed * 0.25) * 0.05)
      this.active.mesh.program.uniforms.uBlurryObjectPositionAnimation1.value.set(Math.sin(this.gl.time.elapsed * 0.5) * 0.05, Math.cos(this.gl.time.elapsed * 0.5) * 0.05)
      this.active.mesh.program.uniforms.uBlurryObjectScaleAnimation0.value = Math.sin(this.gl.time.elapsed * 0.5) * 0.05
      this.active.mesh.program.uniforms.uBlurryObjectScaleAnimation1.value = Math.sin(-this.gl.time.elapsed) * 0.05
    }

    this.active.mesh.program.uniforms.uTime.value = this.gl.time.elapsed * 0.05

    // this.object.position.x = Math.sin(this.gl.time.elapsed)
    // this.object.position.y = Math.cos(this.gl.time.elapsed)
  }
}
